/**
 * Providers and Models' forms (docs/plans/2026-10-assurance-assessments.md): an organisation's assessment of a row,
 * a provider of its own, a row's history, and Mojo Up's editor for the register it publishes. Every form says in
 * plain words what the rating rests on, criterion by criterion, as the register's rows are rated.
 */

import {
  ASSURANCE_DESCRIPTIONS,
  ASSURANCE_EVIDENCE_TYPES,
  ASSURANCE_ORDER,
  ASSURANCE_TIERS,
  ASSURANCE_WORDS,
  type AssessmentRegion,
  type AssuranceAssessment,
  type AssuranceAssessmentInput,
  type AssuranceBasis,
  type AssuranceEvidence,
  type AssuranceMatch,
  type AssuranceRating,
  type AssuranceRegisterEdit,
  type AssuranceRow,
} from '@aioe/shared';
import { dateMedium } from '@aioe/shared/wording/dateTime';
import { Button, Modal } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { type ReactNode, useRef, useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { providerWord, RatingMark, rowWord, tierWord } from './assurance.tsx';
import { ChoiceSelect, ErrorNote, inputClass, StatusMark } from './ui.tsx';

// ── Words ─────────────────────────────────────────────────────────────

const REGIONS: Record<AssessmentRegion, string> = { au: 'Australia', nz: 'New Zealand', us: 'United States', eu: 'European Union', uk: 'United Kingdom', local: 'On your own machines', other: 'Somewhere else', unknown: 'Not known' };
const REGION_IN: Record<AssessmentRegion, string> = { au: 'in Australia', nz: 'in New Zealand', us: 'in the United States', eu: 'in the European Union', uk: 'in the United Kingdom', local: 'on your own machines', other: 'elsewhere', unknown: '' };
const TERMS: Record<AssuranceBasis['terms']['answer'], string> = { contract: 'A contract', 'business-terms': 'Business terms', 'personal-terms': 'Personal terms', unknown: 'Not known' };

const dateWords = (iso: string) => {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return Number.isNaN(d.getTime()) ? iso : dateMedium(d, { timeZone: iso.length === 10 ? 'UTC' : undefined });
};

const NOTHING = 'Nothing recorded of what it rests on.';

/** What an assessment rests on, as one sentence: only what the assessor answered. */
export function basisSentence(b: AssuranceBasis): string {
  const parts: string[] = [];
  const { processedIn, storedIn } = b.residency;
  if (processedIn !== 'unknown' && processedIn === storedIn) parts.push(`processed and stored ${REGION_IN[processedIn]}`);
  else {
    if (processedIn !== 'unknown') parts.push(`processed ${REGION_IN[processedIn]}`);
    if (storedIn !== 'unknown') parts.push(`stored ${REGION_IN[storedIn]}`);
  }
  if (b.training.answer === 'no') parts.push('inputs never used for training');
  if (b.training.answer === 'yes') parts.push('inputs may be used for training');
  if (b.retention.days !== null) parts.push(b.retention.days === 0 ? 'nothing kept' : `kept ${b.retention.days} days`);
  if (b.terms.answer === 'contract') parts.push('under a contract');
  if (b.terms.answer === 'business-terms') parts.push('under business terms');
  if (b.terms.answer === 'personal-terms') parts.push('under personal terms');
  if (b.isolation.answer === 'isolated') parts.push('isolated from other customers');
  if (b.isolation.answer === 'shared') parts.push('shared with other customers');
  if (!parts.length) return NOTHING;
  const s = parts.join('; ');
  return `${s[0]!.toUpperCase()}${s.slice(1)}.`;
}

/** What an assessment rests on, quiet when nothing was recorded. */
export function BasisLine({ basis }: { basis: AssuranceBasis }) {
  const s = basisSentence(basis);
  return <p className={`text-sm ${s === NOTHING ? 'text-muted' : ''}`}>{s}</p>;
}

/** The kinds of call a provider of the organisation's own is reached by, in the workbench's ids. */
const KINDS: Array<{ key: string; label: string; ids: string[] }> = [
  { key: 'azure', label: 'Azure OpenAI', ids: ['azure', 'azure-openai'] },
  { key: 'bedrock', label: 'Amazon Bedrock', ids: ['bedrock'] },
  { key: 'compatible', label: 'An OpenAI-compatible endpoint (a gateway, a reseller)', ids: ['openai-compatible'] },
  { key: 'server', label: 'A model server on your own machines', ids: ['vllm', 'ollama', 'lmstudio', 'llamacpp'] },
  { key: 'openai', label: 'OpenAI', ids: ['openai', 'codex'] },
  { key: 'anthropic', label: 'Anthropic', ids: ['anthropic', 'claude-code'] },
  { key: 'gemini', label: 'Google Gemini', ids: ['gemini'] },
  { key: 'openrouter', label: 'OpenRouter', ids: ['openrouter'] },
  { key: 'other', label: 'Another, by its id in the workbench', ids: [] },
];
const KIND_OPTIONS = Object.fromEntries(KINDS.map((k) => [k.key, k.label])) as Record<string, string>;
const kindOf = (providers: string[]) => KINDS.find((k) => k.ids.length && k.ids.length === providers.length && k.ids.every((id) => providers.includes(id)))?.key ?? 'other';
const list = (text: string) =>
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

/** What a row applies to, in words. */
export function matchSentence(m: AssuranceMatch): string {
  const kind = KINDS.find((k) => k.key === kindOf(m.providers));
  const via = kind && kind.key !== 'other' ? kind.label : m.providers.join(', ');
  const hosts = m.hosts?.length ? ` at ${m.hosts.join(', ')}` : ', at any address';
  const models = m.models?.length ? `, for ${m.models.join(', ')}` : '';
  return `Applies to work sent through ${via}${hosts}${models}.`;
}

// ── A form in a modal ─────────────────────────────────────────────────

function FormDialog({ title, blurb, children, submitLabel, busy, error, canSubmit = true, onSubmit, onClose, extra }: { title: string; blurb?: ReactNode; children: ReactNode; submitLabel: string; busy?: boolean; error?: string; canSubmit?: boolean; onSubmit: () => void; onClose: () => void; extra?: ReactNode }) {
  return (
    <Modal isOpen onOpenChange={(open) => !open && !busy && onClose()}>
      <Modal.Backdrop isKeyboardDismissDisabled={busy}>
        <Modal.Container placement="center" size="lg" scroll="inside">
          <Modal.Dialog aria-label={title} className="sm:max-w-2xl">
            <Modal.CloseTrigger />
            <form
              className="contents"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmit && !busy) onSubmit();
              }}
            >
              <Modal.Header className="flex-col items-start gap-1">
                <Modal.Heading>{title}</Modal.Heading>
                {blurb ? <p className="text-sm text-muted">{blurb}</p> : null}
              </Modal.Header>
              <Modal.Body className="grid gap-4 p-1">
                {children}
                <ErrorNote error={error} />
              </Modal.Body>
              <Modal.Footer className="flex items-center gap-2">
                <div className="mr-auto">{extra}</div>
                <Button variant="tertiary" onPress={onClose} isDisabled={busy}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isPending={busy} isDisabled={!canSubmit}>
                  {submitLabel}
                </Button>
              </Modal.Footer>
            </form>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="grid min-w-0 gap-1 text-xs text-muted">
      {label}
      {children}
      {hint ? <span>{hint}</span> : null}
    </label>
  );
}

/** One criterion: its question, the answer, and the assessor's own words. */
function Criterion({ title, answer, words, onWords, placeholder }: { title: string; answer: ReactNode; words: string; onWords: (v: string) => void; placeholder: string }) {
  return (
    <fieldset className="grid gap-2 border-t border-separator pt-3 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <span className="text-sm font-medium">{title}</span>
        {answer}
      </div>
      <input className={inputClass} aria-label={`${title}: in your words`} value={words} onChange={(e) => onWords(e.target.value)} maxLength={500} placeholder={placeholder} />
    </fieldset>
  );
}

function Choice<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: Record<T, string>; onChange: (v: T) => void }) {
  return (
    <Segment aria-label={label} size="sm" selectedKey={value} onSelectionChange={(k) => onChange(String(k) as T)} className="w-fit">
      {(Object.keys(options) as T[]).map((k) => (
        <Segment.Item key={k} id={k}>
          {options[k]}
        </Segment.Item>
      ))}
    </Segment>
  );
}

// ── The assessment's fields ───────────────────────────────────────────

const yearAhead = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

/** The draft of an assessment, from the one it replaces (or a blank one). */
export function useAssessmentDraft(from?: AssuranceAssessment, rating: AssuranceRating = 'P') {
  const [draft, setDraft] = useState<AssuranceAssessmentInput>(() => ({
    rating: from?.rating ?? rating,
    basis: structuredClone(from?.basis ?? { residency: { processedIn: 'unknown', storedIn: 'unknown', words: '' }, training: { answer: 'unknown', words: '' }, retention: { days: null, words: '' }, terms: { answer: 'unknown', words: '' }, isolation: { answer: 'unknown', words: '' } }),
    evidence: from?.evidence ? [...from.evidence] : [],
    reviewBy: yearAhead(),
    note: from?.note ?? '',
  }));
  const setBasis = <K extends keyof AssuranceBasis>(k: K, v: Partial<AssuranceBasis[K]>) => setDraft((d) => ({ ...d, basis: { ...d.basis, [k]: { ...d.basis[k], ...v } } }));
  return { draft, setDraft, setBasis };
}

type Draft = ReturnType<typeof useAssessmentDraft>;

export function AssessmentFields({ d, canUpload, mojoUp }: { d: Draft; canUpload: boolean; mojoUp?: AssuranceRating }) {
  const { draft, setDraft, setBasis } = d;
  const b = draft.basis;
  const ratings = Object.fromEntries(ASSURANCE_ORDER.map((r) => [r, ASSURANCE_WORDS[r]])) as Record<AssuranceRating, string>;
  const regions = REGIONS;
  return (
    <>
      <div className="grid gap-1">
        <Field label="Your rating">
          <ChoiceSelect label="Your rating" value={draft.rating} options={ratings} onChange={(rating) => setDraft((x) => ({ ...x, rating }))} className="w-full" />
        </Field>
        <span className="text-xs text-muted">
          {ASSURANCE_DESCRIPTIONS[draft.rating]}
          {mojoUp ? ` Mojo Up rates it ${ASSURANCE_WORDS[mojoUp]}.` : ''}
        </span>
      </div>
      <section className="grid gap-3">
        <span className="pane-label">What it rests on</span>
        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">Where data is processed and stored</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Processed in">
              <ChoiceSelect label="Processed in" value={b.residency.processedIn} options={regions} onChange={(processedIn) => setBasis('residency', { processedIn })} className="w-full" />
            </Field>
            <Field label="Stored in">
              <ChoiceSelect label="Stored in" value={b.residency.storedIn} options={regions} onChange={(storedIn) => setBasis('residency', { storedIn })} className="w-full" />
            </Field>
          </div>
          <input className={inputClass} aria-label="Where data goes: in your words" value={b.residency.words} onChange={(e) => setBasis('residency', { words: e.target.value })} maxLength={500} placeholder="A regional deployment in australiaeast; logs stay in the same region" />
        </fieldset>
        <Criterion title="Inputs used for training" answer={<Choice label="Inputs used for training" value={b.training.answer} options={{ no: 'Never', yes: 'They may be', unknown: 'Not known' }} onChange={(answer) => setBasis('training', { answer })} />} words={b.training.words} onWords={(words) => setBasis('training', { words })} placeholder="The agreement's clause that says so" />
        <Criterion
          title="How long inputs are kept"
          answer={
            <span className="flex items-center gap-2 text-sm">
              <input className={`${inputClass} w-24 tabular-nums`} aria-label="Days kept" type="number" min={0} max={36500} value={b.retention.days ?? ''} placeholder="Not known" onChange={(e) => setBasis('retention', { days: e.target.value === '' ? null : Math.max(0, Math.min(36500, Math.round(Number(e.target.value)))) })} />
              days
            </span>
          }
          words={b.retention.words}
          onWords={(words) => setBasis('retention', { words })}
          placeholder="Thirty days for abuse monitoring, then deleted"
        />
        <Criterion title="The contract or terms" answer={<ChoiceSelect label="The contract or terms" value={b.terms.answer} options={TERMS} onChange={(answer) => setBasis('terms', { answer })} />} words={b.terms.words} onWords={(words) => setBasis('terms', { words })} placeholder="Our enterprise agreement, signed March 2026" />
        <Criterion title="Kept apart from other customers" answer={<Choice label="Kept apart from other customers" value={b.isolation.answer} options={{ isolated: 'Yes', shared: 'No', unknown: 'Not known' }} onChange={(answer) => setBasis('isolation', { answer })} />} words={b.isolation.words} onWords={(words) => setBasis('isolation', { words })} placeholder="A dedicated resource in our own subscription" />
      </section>
      <EvidenceFields evidence={draft.evidence} onChange={(evidence) => setDraft((x) => ({ ...x, evidence }))} canUpload={canUpload} />
      <div className="grid gap-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
        <Field label="Review by">
          <input className={inputClass} type="date" value={draft.reviewBy ?? ''} onChange={(e) => setDraft((x) => ({ ...x, ...(e.target.value ? { reviewBy: e.target.value } : { reviewBy: undefined }) }))} />
        </Field>
        <Field label="Note">
          <input className={inputClass} value={draft.note} onChange={(e) => setDraft((x) => ({ ...x, note: e.target.value }))} maxLength={500} placeholder="Reviewed with legal; ask again when the agreement renews" />
        </Field>
      </div>
    </>
  );
}

function EvidenceFields({ evidence, onChange, canUpload }: { evidence: AssuranceEvidence[]; onChange: (e: AssuranceEvidence[]) => void; canUpload: boolean }) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const file = useRef<HTMLInputElement>(null);
  const addLink = () => {
    const u = url.trim();
    if (!/^https:\/\/[^\s/]+/.test(u)) return setError('A link starts with https://');
    setError(undefined);
    onChange([...evidence, { kind: 'link', url: u, label: label.trim() }]);
    setUrl('');
    setLabel('');
  };
  const upload = async (f: File | undefined) => {
    if (!f) return;
    try {
      setBusy(true);
      setError(undefined);
      const doc = await routes.uploadAssuranceEvidence(f);
      if (!evidence.some((e) => e.kind === 'document' && e.id === doc.id)) onChange([...evidence, { kind: 'document', id: doc.id, name: doc.name, contentType: doc.contentType, size: doc.size }]);
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
      if (file.current) file.current.value = '';
    }
  };
  return (
    <section className="grid gap-2">
      <span className="pane-label">Evidence</span>
      {evidence.length ? (
        <ul className="grid gap-1">
          {evidence.map((e, i) => (
            <li key={i} className="flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0 truncate">{e.kind === 'link' ? e.label || e.url : e.name}</span>
              <Button size="sm" variant="ghost" onPress={() => onChange(evidence.filter((_, j) => j !== i))}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted">A link to the agreement, the provider's data-handling page or your review, {canUpload ? 'or the document itself' : 'as many as ten'}.</p>
      )}
      {evidence.length < 10 ? (
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_10rem_auto]">
          <input className={inputClass} aria-label="Evidence link" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLink())} />
          <input className={inputClass} aria-label="What the link is" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="What it is" maxLength={200} />
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" isDisabled={!url.trim()} onPress={addLink}>
              Add link
            </Button>
            {canUpload ? (
              <>
                <input ref={file} type="file" className="hidden" accept={ASSURANCE_EVIDENCE_TYPES.join(',')} onChange={(e) => void upload(e.target.files?.[0])} />
                <Button size="sm" variant="secondary" isPending={busy} onPress={() => file.current?.click()}>
                  Upload
                </Button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
      <ErrorNote error={error} />
    </section>
  );
}

// ── Assess a row ──────────────────────────────────────────────────────

/** The rating Mojo Up gives a row, under the organisation's assessment. */
export const mojoUpRatingOf = (r: AssuranceRow): AssuranceRating => (r.overridden && r.builtInRating ? r.builtInRating : r.rating);

export function AssessDialog({ row, canUpload, onClose, onSaved }: { row: AssuranceRow; canUpload: boolean; onClose: () => void; onSaved: () => void }) {
  const own = row.origin === 'organisation';
  const d = useAssessmentDraft(row.assessment, own ? row.rating : mojoUpRatingOf(row));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const save = async () => {
    try {
      setBusy(true);
      setError(undefined);
      await routes.assess(row.provider, row.tier, d.draft);
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormDialog
      title={`Assess ${rowWord(row)}, ${tierWord(row.tier)}`}
      blurb={own ? 'Your organisation’s own provider: its rating is yours alone. Saved as a new assessment; the last one stays in its history.' : 'Your assessment stands over Mojo Up’s rating for your organisation only. Saved as a new assessment; the last one stays in its history and the audit trail.'}
      submitLabel="Save assessment"
      busy={busy}
      error={error}
      onSubmit={() => void save()}
      onClose={onClose}
    >
      <AssessmentFields d={d} canUpload={canUpload} {...(own ? {} : { mojoUp: mojoUpRatingOf(row) })} />
    </FormDialog>
  );
}

// ── The organisation's own provider ───────────────────────────────────

function MatchFields({ match, onChange }: { match: { kind: string; ids: string; hosts: string; models: string }; onChange: (m: { kind: string; ids: string; hosts: string; models: string }) => void }) {
  return (
    <section className="grid gap-3">
      <span className="pane-label">What it applies to</span>
      <Field label="Reached as">
        <ChoiceSelect label="Reached as" value={match.kind} options={KIND_OPTIONS} onChange={(kind) => onChange({ ...match, kind })} className="w-full" />
      </Field>
      {match.kind === 'other' ? (
        <Field label="Its id in the workbench" hint="As the workbench names the provider, such as deepseek or mistral. Several, separated by commas.">
          <input className={`${inputClass} mono`} value={match.ids} onChange={(e) => onChange({ ...match, ids: e.target.value })} placeholder="mistral" />
        </Field>
      ) : null}
      <Field label="At these addresses" hint="The host each request goes to; *.example.com for any address under it. Left empty, any address.">
        <input className={`${inputClass} mono`} value={match.hosts} onChange={(e) => onChange({ ...match, hosts: e.target.value })} placeholder="contoso-au.openai.azure.com" />
      </Field>
      <Field label="For these models" hint="Left empty, every model. * matches anything, as in gpt-5*.">
        <input className={`${inputClass} mono`} value={match.models} onChange={(e) => onChange({ ...match, models: e.target.value })} placeholder="Every model" />
      </Field>
    </section>
  );
}

const matchDraft = (m?: AssuranceMatch) => {
  const kind = m ? kindOf(m.providers) : 'azure';
  return { kind, ids: kind === 'other' && m ? m.providers.join(', ') : '', hosts: m?.hosts?.join(', ') ?? '', models: m?.models?.join(', ') ?? '' };
};
const matchFrom = (m: ReturnType<typeof matchDraft>): AssuranceMatch => {
  const providers = m.kind === 'other' ? list(m.ids) : (KINDS.find((k) => k.key === m.kind)?.ids ?? []);
  const hosts = list(m.hosts).map((h) => h.toLowerCase());
  const models = list(m.models);
  return { providers, ...(hosts.length ? { hosts } : {}), ...(models.length ? { models } : {}) };
};

const TIER_OPTIONS = Object.fromEntries(ASSURANCE_TIERS.map((t) => [t, tierWord(t)[0]!.toUpperCase() + tierWord(t).slice(1)])) as Record<(typeof ASSURANCE_TIERS)[number], string>;

export function AddProviderDialog({ canUpload, onClose, onSaved }: { canUpload: boolean; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [tier, setTier] = useState<(typeof ASSURANCE_TIERS)[number]>('enterprise');
  const [match, setMatch] = useState(matchDraft());
  const [note, setNote] = useState('');
  const d = useAssessmentDraft(undefined, 'P');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const m = matchFrom(match);
  const save = async () => {
    try {
      setBusy(true);
      setError(undefined);
      await routes.addAssuranceProvider({ name: name.trim(), tier, match: m, note: note.trim(), assessment: d.draft });
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormDialog
      title="Add a provider"
      blurb="A provider the register does not have: your own Azure deployment, an internal model gateway, a regional reseller or a model server on your premises. Your rating applies to your organisation’s work that goes to it."
      submitLabel="Add provider"
      busy={busy}
      error={error}
      canSubmit={!!name.trim() && m.providers.length > 0}
      onSubmit={() => void save()}
      onClose={onClose}
    >
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
        <Field label="Name">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="Contoso’s Azure OpenAI in Sydney" />
        </Field>
        <Field label="Tier">
          <ChoiceSelect label="Tier" value={tier} options={TIER_OPTIONS} onChange={setTier} className="w-full" />
        </Field>
      </div>
      <MatchFields match={match} onChange={setMatch} />
      <Field label="About it">
        <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Run by the platform team; requests go through our private network" />
      </Field>
      <AssessmentFields d={d} canUpload={canUpload} />
    </FormDialog>
  );
}

export function EditProviderDialog({ row, onClose, onSaved }: { row: AssuranceRow; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(row.name ?? '');
  const [match, setMatch] = useState(matchDraft(row.match));
  const [note, setNote] = useState(row.note);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const m = matchFrom(match);
  const save = async () => {
    try {
      setBusy(true);
      setError(undefined);
      await routes.changeAssuranceProvider(row.provider, row.tier, { name: name.trim(), match: m, note: note.trim() });
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormDialog title={`Change ${rowWord(row)}`} blurb="Its name, and the work its rating applies to. The change is recorded in the audit trail." submitLabel="Save" busy={busy} error={error} canSubmit={!!name.trim() && m.providers.length > 0} onSubmit={() => void save()} onClose={onClose}>
      <Field label="Name">
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
      </Field>
      <MatchFields match={match} onChange={setMatch} />
      <Field label="About it">
        <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
      </Field>
    </FormDialog>
  );
}

// ── History ───────────────────────────────────────────────────────────

export function EvidenceList({ evidence }: { evidence: AssuranceEvidence[] }) {
  if (!evidence.length) return null;
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
      {evidence.map((e, i) => (
        <li key={i} className="min-w-0">
          {e.kind === 'link' ? (
            <a className="text-accent hover:underline" href={e.url} target="_blank" rel="noopener noreferrer">
              {e.label || new URL(e.url).hostname}
            </a>
          ) : (
            <button type="button" className="cursor-[var(--cursor-interactive)] text-accent hover:underline" onClick={() => void routes.downloadAssuranceEvidence(e.id, e.name)}>
              {e.name}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Who and when, and when it is to be reviewed. */
export function assessedLine(a: AssuranceAssessment): string {
  const who = a.legacy ? 'Recorded before assessments, as a rating alone' : `Assessed ${dateWords(a.assessedAt)}${a.assessedByName ? ` by ${a.assessedByName}` : ''}`;
  return `${who}${a.reviewBy ? ` · review by ${dateWords(a.reviewBy)}` : ''}`;
}

export function HistoryDialog({ row, onClose }: { row: AssuranceRow; onClose: () => void }) {
  const data = useLoad(() => routes.assuranceHistory(row.provider, row.tier), [row.provider, row.tier]);
  const items = data.data?.assessments ?? [];
  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Backdrop>
        <Modal.Container placement="center" size="md" scroll="inside">
          <Modal.Dialog aria-label={`History of ${rowWord(row)}`}>
            <Modal.CloseTrigger />
            <Modal.Header className="flex-col items-start gap-1">
              <Modal.Heading>
                {rowWord(row)}, {tierWord(row.tier)}
              </Modal.Heading>
              <p className="text-sm text-muted">Every assessment your organisation has made of it, newest first.</p>
            </Modal.Header>
            <Modal.Body className="grid gap-3 p-1">
              <ErrorNote error={data.error} />
              {!data.data && !data.error ? <p className="text-sm text-muted">Loading…</p> : null}
              {data.data && !items.length ? <p className="text-sm text-muted">Not assessed yet.</p> : null}
              <ol className="grid gap-3">
                {items.map((a, i) => (
                  <li key={a.id} className="grid gap-1 border-t border-separator pt-3 first:border-t-0 first:pt-0">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <RatingMark rating={a.rating} />
                      {a.withdrawnAt ? <StatusMark state="disabled" label={`Withdrawn ${dateWords(a.withdrawnAt)}`} /> : i === 0 ? <StatusMark state="connected" label="In force" /> : null}
                    </div>
                    <BasisLine basis={a.basis} />
                    {a.note ? <p className="text-sm text-muted">{a.note}</p> : null}
                    <p className="text-xs text-muted">{assessedLine(a)}</p>
                    <EvidenceList evidence={a.evidence} />
                  </li>
                ))}
              </ol>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="tertiary" onPress={onClose}>
                Close
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

// ── Mojo Up's editor ──────────────────────────────────────────────────

/** Mojo Up's organisation on Cloud: a row of the register it publishes, whole. `row` undefined adds one. */
export function RegisterRowDialog({ row, onClose, onSaved }: { row?: AssuranceRow; onClose: () => void; onSaved: () => void }) {
  const [provider, setProvider] = useState(row?.provider ?? '');
  const [tier, setTier] = useState<(typeof ASSURANCE_TIERS)[number]>((row?.tier as (typeof ASSURANCE_TIERS)[number]) ?? 'api');
  const [name, setName] = useState(row?.name ?? '');
  const [rating, setRating] = useState<AssuranceRating>(row?.rating ?? 'P');
  const [region, setRegion] = useState(row?.region ?? 'unknown');
  const [training, setTraining] = useState(row ? (row.trainsOnInputs ? 'yes' : 'no') : 'no');
  const [isolation, setIsolation] = useState(row ? (row.tenantIsolation ? 'yes' : 'no') : 'no');
  const [contract, setContract] = useState(row ? (row.contract ? 'yes' : 'no') : 'yes');
  const [days, setDays] = useState(row?.retentionDays ?? null);
  const [note, setNote] = useState(row?.note ?? '');
  const [match, setMatch] = useState(matchDraft(row?.match));
  const [withMatch, setWithMatch] = useState(!!row?.match);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const ratings = Object.fromEntries(ASSURANCE_ORDER.map((r) => [r, ASSURANCE_WORDS[r]])) as Record<AssuranceRating, string>;
  const m = matchFrom(match);
  const id = provider.trim().toLowerCase();
  const save = async () => {
    try {
      setBusy(true);
      setError(undefined);
      const body: AssuranceRegisterEdit = {
        rating,
        region: region.trim() || 'unknown',
        trainsOnInputs: training === 'yes',
        tenantIsolation: isolation === 'yes',
        retentionDays: days,
        contract: contract === 'yes',
        note: note.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(withMatch && m.providers.length ? { match: m } : {}),
      };
      await routes.editRegisterRow(id, tier, body);
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const yesNo = { yes: 'Yes', no: 'No' };
  return (
    <FormDialog
      title={row ? `Edit ${rowWord(row)}, ${tierWord(row.tier)}` : 'Add a row to the register'}
      blurb="Published at once as a new revision: every organisation on AI Cloud has it now, and each Enterprise deployment with its next release check."
      submitLabel={row ? 'Publish' : 'Add and publish'}
      busy={busy}
      error={error}
      canSubmit={!!id && !!note.trim()}
      onSubmit={() => void save()}
      onClose={onClose}
    >
      {row ? null : (
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
          <Field label="Provider id" hint="Lower case, as the workbench names it: bedrock, mistral.">
            <input className={`${inputClass} mono`} value={provider} onChange={(e) => setProvider(e.target.value)} maxLength={100} placeholder="bedrock" />
          </Field>
          <Field label="Tier">
            <ChoiceSelect label="Tier" value={tier} options={TIER_OPTIONS} onChange={setTier} className="w-full" />
          </Field>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name to show">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder={row ? providerWord(row.provider) : 'Amazon Bedrock'} />
        </Field>
        <Field label="Rating">
          <ChoiceSelect label="Rating" value={rating} options={ratings} onChange={setRating} className="w-full" />
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Where data is processed" hint="au, us, eu, local or unknown">
          <input className={`${inputClass} mono`} value={region} onChange={(e) => setRegion(e.target.value)} maxLength={20} />
        </Field>
        <Field label="Days inputs are kept" hint="Empty when not known">
          <input className={`${inputClass} tabular-nums`} type="number" min={0} value={days ?? ''} onChange={(e) => setDays(e.target.value === '' ? null : Math.max(0, Math.round(Number(e.target.value))))} />
        </Field>
      </div>
      <div className="grid gap-2">
        {(
          [
            ['Inputs used for training', training, setTraining],
            ['Kept apart from other customers', isolation, setIsolation],
            ['Bound by a contract', contract, setContract],
          ] as const
        ).map(([label, value, set]) => (
          <div key={label} className="flex items-center justify-between gap-3 text-sm">
            <span>{label}</span>
            <Choice label={label} value={value} options={yesNo} onChange={set} />
          </div>
        ))}
      </div>
      <Field label="What the rating depends on">
        <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Business terms: no training on inputs; processing offshore unless residency is bought" />
      </Field>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span>Name the work it applies to</span>
        <Choice label="Name the work it applies to" value={withMatch ? 'yes' : 'no'} options={yesNo} onChange={(v) => setWithMatch(v === 'yes')} />
      </div>
      {withMatch ? <MatchFields match={match} onChange={setMatch} /> : <p className="-mt-2 text-xs text-muted">A provider the workbench already knows by its id needs nothing here.</p>}
    </FormDialog>
  );
}
